import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce, MenuItem, Popover, useMediaQuery } from '@mui/material';
import { Button, Label } from 'flowbite-react';
import { IoChevronDown } from 'react-icons/io5';
import classNames from 'classnames';
import { useThemeContext } from 'src/context/ThemeProvider';
import { MenuOptionsType } from 'src/components/recruiterPositions/RecruiterCandidateCardFooter';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { Modal } from 'flowbite-react';
import {
	CompanyDocument,
	CompanyTypeForDrivePositions,
	DriveInviteeStatus,
	OfflineDriveCandidateStatus,
	UIOfflineDriveRoom,
} from 'types';
import { useGetOfflineDriveRooms } from 'src/hooks/offlineDrive.hooks';
import { useMarkAttendanceMutation } from 'src/hooks/utils/mutations/offlineDriveMutations/useMarkAttendanceMutation.hooks';
import { Drawer } from 'src/components/shared/Drawer';
const CompaniesActions = ({
	driveId,
	positionId,
	companyId,
	companyName,
	company,
}: {
	driveId: string;
	positionId: string;
	companyId: string;
	companyName: string;
	company: CompanyTypeForDrivePositions;
}) => {
	const isMoblie = useMediaQuery('(max-width:960px)');
	const [searchParams] = useSearchParams();
	const [selectedRoom, setSelectedRoom] = useState<UIOfflineDriveRoom>();
	const [selectedCity, setSelectedCity] = useState<string>("bangalore");
	const navigate = useNavigate();
	const { themeMode } = useThemeContext();
	const bulkSelect = searchParams.get('bulkSelect') === 'true';
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const id = open ? 'simple-popover' : undefined;
	const [openMarkAttendanceDrawer, setOpenMarkAttendanceDrawer] = useState<string | null>(null);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const [menuOptions, setMenuOptions] = useState<MenuOptionsType[]>([]);
	const { data: offlineDriveRoomsData, isLoading } = useGetOfflineDriveRooms({});

	const handleClose = () => {
		setAnchorEl(null);
	};

	useEffect(() => {
		const options: MenuOptionsType[] = [
			{
				key: 'mark-attendance',
				label: 'Mark Attendance',
				hide:
					company.driveInvitees?.offlineDriveCandidateStatus === OfflineDriveCandidateStatus.PRESENT,
				onClick: () => {
					setOpenMarkAttendanceDrawer('mark-attendance');
				},
			},
			{
				key: 'change-room',
				label: company?.offlineDriveRoom ? 'Change Room' : 'Assign Room',
				hide:
					!company.driveInvitees ||
					(company.driveInvitees?.offlineDriveCandidateStatus !== OfflineDriveCandidateStatus.PRESENT &&
						!!company?.driveInvitees?.offlineDriveRoom),
				onClick: () => {
					setOpenMarkAttendanceDrawer('change-room');
				},
			},
			{
				key: 'view-shortlisted-candidates',
				label: 'View Shortlisted Candidates',
				onClick: () => {
					navigate(`/offline-drive-companies/${positionId}?driveId=${driveId}`);
				},
			},
		];
		setMenuOptions(options);
	}, [company]);

	const markAttendanceMutation = useMarkAttendanceMutation();
	const cities = useMemo(() => {
		const allCities = offlineDriveRoomsData?.data?.map(room => room.city) || [];
		return Array.from(new Set(allCities));
	}, [offlineDriveRoomsData]);

	return (
		<>
			<Button
				color="purplePlaced"
				disabled={bulkSelect}
				onClick={handleClick}
				className="[&_span]:!p-0 !p-0 border-[#E5E7EB] whitespace-pre"
			>
				<p className="px-[10px] py-[8px]">Details</p>
				<IoChevronDown
					className={classNames('w-[34px] h-[34px] px-[8px] transition-transform', open ? 'rotate-[180deg]' : '')}
				/>
			</Button>

			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				sx={{
					'&>.MuiPaper-root': {
						borderRadius: '8px',
						background: themeMode === 'dark' ? '#18222D' : 'white',
						boxShadow: '0px 1px 2px -1px rgba(0, 0, 0, 0.10), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)',
					},
					'&>.MuiPaper-root>.MuiButtonBase-root': {
						minHeight: '40px',
					},
				}}
			>
				{menuOptions &&
					menuOptions
						.filter(el => !el.hide)
						.map((menuOption: MenuOptionsType, index: number) => (
							<MenuItem
								key={index}
								className={'dark:!text-[#C7C9D9] dark:hover:!bg-[#2c3c50]'}
								style={{
									padding: '8px 16px',
								}}
								onClick={event => {
									if (menuOption.onClick) {
										menuOption.onClick(event);
									}
									handleClose();
								}}
							>
								{menuOption.label}
							</MenuItem>
						))}
			</Popover>

			<Drawer
				minHeight="auto"
				fullWidth={true}
				isModal={!isMoblie}
				isOpen={!!openMarkAttendanceDrawer}
				onClose={() => setOpenMarkAttendanceDrawer(null)}
				header={
					<div className="px-3 py-3 flex flex-row gap-x-8">
						<h1 className="text-[#1C1C28] dark:text-inherit text-base md:text-xl font-inter font-[500]">
							{openMarkAttendanceDrawer === 'mark-attendance'
								? 'Mark Attendance:'
								: !company?.offlineDriveRoom
									? 'Assign Room'
									: 'Change Room:'}{' '}
							{companyName}
						</h1>
						<p className="text-red-500 dark:text-inherit text-sm font-inter font-[500] "></p>
						<Select
							required
							options={cities.map(c => ({
								label: c.toLocaleUpperCase(),
								value: c,
								data: c,
							}))}
							menuPosition="fixed"
							isLoading={isLoading}
							menuPlacement={'auto'}
							placeholder="Select City"
							classNamePrefix="my-select-plans"
							defaultValue={{ label: 'BANGALORE', value: 'bangalore', data: 'bangalore' }}
							onChange={e => {
								setSelectedCity(e?.value || "bangalore");
							}}
						/>
					</div>
				}

				body={
					<div className="pb-4 px-3">
						<div className="mb-[8px]">
							<Label>{'Select Room '}</Label>
							<span className="text-[red]">*</span>
						</div>

						<Select
							required
							options={offlineDriveRoomsData?.data
								?.filter(c => c.city === selectedCity && !c?.isArchived)
								?.map(c => ({
									label: `Room: ${c.roomName}, Floor: ${c.floorName}, Building: ${c.buildingName}, City: ${c.city}`,
									value: c._id,
									data: c,
								}))}
							menuPosition="fixed"
							isLoading={isLoading}
							menuPlacement={'auto'}
							placeholder="Select Room"
							classNamePrefix="flowbite-select"
							onChange={selection => {
								if (selection && selection.data) {
									setSelectedRoom(selection?.data as UIOfflineDriveRoom);
								}
							}}
						/>
						<Button
							onClick={() => {
								markAttendanceMutation.mutate({
									isCompany: true,
									offlineDriveRoom: selectedRoom?._id,
									companyIds: [companyId],
									driveId,
									status: OfflineDriveCandidateStatus.PRESENT,
								});
								setOpenMarkAttendanceDrawer(null);
							}}
							disabled={!selectedRoom || markAttendanceMutation.isLoading}
							color="purplePlaced"
							className="w-full mt-4"
						>
							Mark as present
						</Button>
					</div>
				}
			/>
		</>
	);
};

export default CompaniesActions;
